// R8 Phase 1b — imperative error-message translator.
//
// friendlyMessage (catalog.ts) is a PURE module function called from
// non-React code (toastStore, wsManager request rejection, stores), so it
// cannot use the useTranslations hook. Since Phase 1c this module is a thin
// `errors.*`-namespace view over the whole-catalog bridge in
// '@/lib/i18n/imperative' — one shared bridge state, one I18nProvider wiring
// point. The Phase 1b call sites (friendlyMessage, failureSummaryI18n, the
// execution store) keep their namespace-relative keys and exact semantics:
// active-locale first, English-catalog fallback, undefined when the key
// exists in neither (the caller then uses its own English literal). Never
// throws, never returns a raw key path.

import {
  type MessageTree,
  resetImperativeMessagesForTest,
  setImperativeMessages,
  translateImperative,
} from '@/lib/i18n/imperative';

/**
 * setErrorMessages installs (or refreshes) the live translator. Called by
 * I18nProvider whenever the active locale/catalog changes (via the shared
 * imperative bridge — this alias remains for the Phase 1b call sites and
 * tests). Idempotent: StrictMode's double-fired effects are harmless.
 */
export function setErrorMessages(locale: string, messages: MessageTree): void {
  setImperativeMessages(locale, messages);
}

/** Test-only: reset the bridge to its pre-hydration state. */
export function resetErrorMessagesForTest(): void {
  resetImperativeMessagesForTest();
}

/**
 * translateError resolves `errors.<key>` in the active locale, falling back
 * to the English catalog, or undefined when the key exists in neither (the
 * caller then uses its own English literal). `values` are ICU args for keys
 * that interpolate (e.g. errors.research.* counts).
 */
export function translateError(
  key: string,
  values?: Record<string, string | number | Date>,
): string | undefined {
  return translateImperative(`errors.${key}`, values);
}
